import { useRef, useState } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Where a signed-in user lands. "/" then sends each role to its own home
 * (member dashboard, group admin pages, super admin pages).
 */
function postLoginPath({ mustChangePassword, mustSelectGroup }) {
  if (mustChangePassword) return '/force-change-password';
  if (mustSelectGroup) return '/select-group';
  return '/';
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  // Set while the form signs in: the form picks the landing page itself
  const signingInRef = useRef(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    signingInRef.current = true;
    try {
      const result = await login(email, password);
      navigate(postLoginPath({
        mustChangePassword: !!result?.must_change_password,
        mustSelectGroup: !!result?.must_select_group,
      }));
    } catch (err) {
      signingInRef.current = false;
      setError(err.response?.data?.message ?? 'Erreur de connexion.');
    } finally {
      setSubmitting(false);
    }
  };

  // Session being restored from a saved token: wait instead of flashing the form
  if (loading && localStorage.getItem('token')) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status" aria-label="Chargement">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  // Already signed in: go where a fresh sign-in would go. The flags come from the
  // sign-in answer or from /auth/me. An expired or invalid token leaves user empty
  // once the session check fails, so the form shows.
  if (user && !loading && !signingInRef.current) {
    return (
      <Navigate
        to={postLoginPath({
          mustChangePassword: !!user.must_change_password,
          mustSelectGroup: !!user.must_select_group,
        })}
        replace
      />
    );
  }

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8 bg-surface">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h1 className="flex justify-center">
          <img src="/brand/m-bao-logo.png" alt="m-bao.fr" width={542} height={320} className="h-auto w-48 sm:w-64 dark:hidden" />
          <img src="/brand/m-bao-logo-on-dark.png" alt="m-bao.fr" width={542} height={320} className="hidden h-auto w-48 sm:w-64 dark:block" />
        </h1>
        <p className="mx-auto mt-2 max-w-68 text-center text-xs font-medium uppercase tracking-[0.2em] text-balance text-primary-900">
          Les bonnes relations font les bonnes affaires.
        </p>
        <h2 className="mt-6 text-center text-2xl/9 font-bold tracking-tight text-skin-primary">
          Connectez-vous à votre compte
        </h2>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
        <div className="bg-surface-secondary px-6 py-12 border border-skin sm:rounded-lg sm:px-12">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="rounded-lg bg-red-50 px-4 py-3 text-sm/6 text-red-700">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm/6 font-medium text-skin-primary">
                Adresse e-mail
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full rounded-md bg-surface-secondary px-3 py-1.5 text-base text-skin-primary border-0 ring-1 ring-skin-strong ring-inset placeholder:text-skin-tertiary focus:ring-2 focus:ring-primary-600 focus:ring-inset sm:text-sm/6"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm/6 font-medium text-skin-primary">
                Mot de passe
              </label>
              <div className="mt-2">
                <input
                  id="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full rounded-md bg-surface-secondary px-3 py-1.5 text-base text-skin-primary border-0 ring-1 ring-skin-strong ring-inset placeholder:text-skin-tertiary focus:ring-2 focus:ring-primary-600 focus:ring-inset sm:text-sm/6"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={submitting}
                className="flex w-full justify-center rounded-md bg-primary-600 px-3 py-1.5 text-sm/6 font-semibold text-white hover:bg-primary-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50"
              >
                {submitting ? 'Connexion...' : 'Se connecter'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
